import { useMemo } from 'react'
import type { LocationCircle } from '@/hooks/useActiveLocations'
import { formatTimeOfDay } from '@/lib/dates'
import {
    buildPathPointDescription,
    calculateRegionFromCoordinates,
    filterMarkersByDistance,
    formatCompactDuration,
    getMinDistanceMeters,
    getStationaryDuration,
} from '@/lib/map-utils'
import { getMovementIcon, getMovementInfo, hasValidLocation } from '@/lib/movement-utils'
import { formatSpeedMph } from '@/lib/units'
import { useMonitorStore } from '@/stores/monitor-store'
import type {
    CircleMarkerData,
    MapCoordinate,
    MapData,
    MapRegion,
    OnSiteClusterData,
    PathMarkerData,
    UserMarkerData,
    UserPathData,
} from '@/types/mapData'
import type { PathPoint, UserLocationWithStatus } from '@/types/movement'
import { useOnSiteGroups } from './useOnSiteGroups'

function buildUserDescription(location: PathPoint, path: PathPoint[]): string {
    const time = formatTimeOfDay(location.timestamp)
    const { label } = getMovementInfo(
        location.is_moving,
        location.movement_type,
        location.heading,
        location.speed
    )
    if (label === 'stationary') {
        const duration = getStationaryDuration(path, location)
        if (duration) return `${time} stationary (${formatCompactDuration(duration)})`
        return `${time} stationary`
    }
    const speedText = formatSpeedMph(location.speed)
    return label ? `${time} ${speedText} (${label})` : `${time} ${speedText}`
}

function computePathMarkerRotation(point: PathPoint): { rotation: number; shouldFlip: boolean } {
    const icon = getMovementIcon(point.movement_type, point.is_moving, point.speed)
    const isNavigationIcon = icon === 'navigation'
    const effectivelyMoving = point.is_moving || point.speed > 2.2
    const rotation =
        !effectivelyMoving || point.movement_type === 'still'
            ? 0
            : isNavigationIcon
              ? point.heading
              : point.heading - 90
    return { rotation, shouldFlip: !isNavigationIcon }
}

function createRegionKey(region: MapRegion): string {
    return `${region.latitude.toFixed(4)},${region.longitude.toFixed(4)},${region.latitudeDelta.toFixed(4)},${region.longitudeDelta.toFixed(4)}`
}

import { variantForStatus } from '@/lib/monitor/markerVariant'

export function useMapData(
    userLocations: UserLocationWithStatus[],
    locationCircles: LocationCircle[],
    options: { clusterOnSite: boolean } = { clusterOnSite: true }
): MapData {
    const zoomLevel = useMonitorStore(state => state.zoomLevel)

    const circles = useMemo((): CircleMarkerData[] => {
        return locationCircles.map(circle => ({
            id: circle.id,
            center: {
                latitude: circle.latitude,
                longitude: circle.longitude,
            },
            radius: circle.radius,
        }))
    }, [locationCircles])

    const visibleUsers = useMemo(() => {
        return userLocations.filter(u => u.visible && hasValidLocation(u))
    }, [userLocations])

    const onSiteClusters = useOnSiteGroups(userLocations, locationCircles)
    const clusteredUserIds = useMemo(() => {
        if (!options.clusterOnSite) return new Set<string>()
        const ids = new Set<string>()
        for (const cluster of onSiteClusters) {
            if (cluster.members.length >= 2) {
                for (const m of cluster.members) ids.add(m.userId)
            }
        }
        return ids
    }, [onSiteClusters, options.clusterOnSite])

    const userMarkers = useMemo((): UserMarkerData[] => {
        const markers: UserMarkerData[] = []
        for (const user of visibleUsers) {
            if (clusteredUserIds.has(user.userId)) continue
            const loc = user.mostRecentLocation
            if (!loc) continue
            const variant = variantForStatus(user.trackingStatus.status)
            if (!variant) continue
            const { icon, rotation } = getMovementInfo(
                loc.is_moving,
                loc.movement_type,
                loc.heading,
                loc.speed
            )
            const shouldFlip = icon !== 'navigation'
            markers.push({
                userId: user.userId,
                userName: user.userName,
                userColor: user.color,
                coordinate: { latitude: loc.latitude, longitude: loc.longitude },
                icon,
                rotation,
                shouldFlip,
                description: buildUserDescription(loc, user.path),
                heading: loc.heading,
                variant,
            })
        }
        return markers
    }, [visibleUsers, clusteredUserIds])

    const minDistanceMeters = getMinDistanceMeters(zoomLevel)

    const userPaths = useMemo((): UserPathData[] => {
        return visibleUsers
            .filter(user => user.path.length > 1)
            .map(user => {
                const coordinates: MapCoordinate[] = user.path.map(p => ({
                    latitude: p.latitude,
                    longitude: p.longitude,
                }))

                const mostRecentTs = user.mostRecentLocation?.timestamp
                const withoutMostRecent = mostRecentTs
                    ? user.path.filter(p => p.timestamp !== mostRecentTs)
                    : user.path

                const allPathMarkers: PathMarkerData[] = withoutMostRecent.map(point => {
                    const icon = getMovementIcon(point.movement_type, point.is_moving, point.speed)
                    const { rotation, shouldFlip } = computePathMarkerRotation(point)

                    return {
                        id: point.id,
                        coordinate: {
                            latitude: point.latitude,
                            longitude: point.longitude,
                        },
                        userName: user.userName,
                        userColor: user.color,
                        icon,
                        rotation,
                        shouldFlip,
                        description: buildPathPointDescription(point),
                    }
                })

                const pathMarkers = filterMarkersByDistance(allPathMarkers, minDistanceMeters)

                const timestamps = user.path.map(p => p.timestamp)

                return {
                    userId: user.userId,
                    userColor: user.color,
                    coordinates,
                    pathMarkers,
                    timestamps,
                }
            })
    }, [visibleUsers, minDistanceMeters])

    const region = useMemo(() => {
        const userCoords = userLocations
            .filter(hasValidLocation)
            .map(
                u =>
                    u.mostRecentLocation as NonNullable<
                        UserLocationWithStatus['mostRecentLocation']
                    >
            )
        const clusterCoords = onSiteClusters.filter(c => c.members.length >= 2).map(c => c.center)
        return calculateRegionFromCoordinates([...userCoords, ...clusterCoords])
    }, [userLocations, onSiteClusters])

    const regionKey = useMemo(() => createRegionKey(region), [region])

    return useMemo(
        () => ({
            region,
            regionKey,
            circles,
            userMarkers,
            userPaths,
            onSiteClusters: options.clusterOnSite
                ? onSiteClusters.filter(c => c.members.length >= 2)
                : [],
        }),
        [region, regionKey, circles, userMarkers, userPaths, onSiteClusters, options.clusterOnSite]
    )
}

export type { OnSiteClusterData }
